import clsx from "clsx";
import {
  ActionButton,
  BannerSeverity,
  isAdult,
  NotificationBanner,
  TravelerSelectStep,
  TravelerSelectWorkflow,
  hasInvalidBirthdate,
} from "halifax";
import React from "react";
import {
  PersonId,
  SELECT_TRAVELERS,
  SELECTED_PACKAGE_PRIMARY_TRAVELER,
} from "redmond";

import {
  FlightPassengerSelectors,
  LodgingSelectors,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector,
  useChildMachineSelector,
} from "@capone/checkout";
import { getConfigTenant, isCorpTenant, useIsTravelApp } from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { personToIPerson } from "../../../../utils/personToIPerson";
import { getUserPassengers } from "../../reducer";
import { Event, TEvent } from "../../state/events";
import { LodgingSelectors as PackageLodgingSelectors } from "../../state/selectors";
import { PackagesMachineContext } from "../../state/types";
import { DesktopPackageBookValidationError } from "../DesktopPackagesBookWorkflow";
import { MobileBookingSteps } from "../MobileBookingSteps";
import { PackagesPriceBreakdown } from "../PriceBreakdown";
import { HotelBookPassengerSelectionConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IHotelBookPassengerSelectionProps
  extends HotelBookPassengerSelectionConnectorProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;

  showNonModalContent?: boolean;
}

export const HotelBookPassengerSelection = ({
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,

  showNonModalContent,
}: IHotelBookPassengerSelectionProps) => {
  const expState = useExperiments();

  const { isTravelApp } = useIsTravelApp();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const [_state, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const isCorporate = isCorpTenant(getConfigTenant());
  const corpTravelers = useSelector(getUserPassengers);
  const travelers = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getUserPassengers
  );

  const selectedFlightTravelerIds = useCheckoutStateSelector(
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent
  );
  const selectedTravelerId = useCheckoutStateSelector(
    PackageLodgingSelectors.getSelectedPassengerIdParent
  );

  const searchedGuestCount = useCheckoutStateSelector(
    LodgingSelectors.getGuestCount
  );

  const selectedTravelerCountDiffersFromSearched =
    selectedFlightTravelerIds.length !== searchedGuestCount;

  const selectablePassengers = React.useMemo(
    () =>
      isCorporate
        ? corpTravelers
        : travelers
            ?.filter(
              (person) =>
                isAdult(person.dateOfBirth) &&
                selectedFlightTravelerIds?.includes(person.id)
            )
            .map(personToIPerson),
    [travelers, selectedFlightTravelerIds, isCorporate, corpTravelers]
  );

  const handleSelectPassenger = (passengerId?: PersonId) => {
    send({
      type: Event.SELECT_PASSENGER,
      passengerId,
      singleTravelerWorkflow: true,
    });
  };

  const canContinue = isMobile || !!selectedTravelerId;

  const handleContinue = () => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
      trackEvent({
        eventName: SELECTED_PACKAGE_PRIMARY_TRAVELER,
        properties: {},
      });
    } else {
      setValidationErrorTypes?.(["hotel-travelers"]);
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  React.useEffect(() => {
    if (
      !selectedTravelerId &&
      selectablePassengers.length === 1 &&
      !hasInvalidBirthdate(selectablePassengers[0].dateOfBirth)
    ) {
      handleSelectPassenger(selectablePassengers[0].id);
    }
  }, [selectedTravelerId, selectablePassengers]);

  React.useEffect(() => {
    if (selectedTravelerId && selectablePassengers.length) {
      // reset hotel passenger if not in selected flight passengers
      if (
        !selectablePassengers.some(
          (passenger) => passenger.id === selectedTravelerId
        )
      ) {
        handleSelectPassenger(undefined);
      }
    }
  }, [selectablePassengers, selectedTravelerId]);

  React.useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  return (
    <>
      <TravelerSelectWorkflow
        readOnly
        hideTravelerActions
        displayModalSubtitle
        showGenderField
        showNationalityField
        singleTravelerWorkflow
        buttonClassName="b2b"
        className={clsx("packages-hotel-book-passenger-selection-root", {
          error: validationErrorTypes?.includes("hotel-travelers"),
        })}
        errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
        isMobile={isMobile}
        progress={
          isMobile ? TravelerSelectStep.TravelerSelect : TravelerSelectStep.Main
        }
        setProgress={() => {}}
        selectedTravelerIds={selectedTravelerId ? [selectedTravelerId] : []}
        setSelectedTravelerIds={(travelerIds: string[]) => {
          trackEvent({
            eventName: SELECT_TRAVELERS,
            properties: {},
          });
          handleSelectPassenger(travelerIds[0]);
        }}
        titles={{
          travelerInfoTitle: isMobile
            ? constants.TRAVELER_INFO_TEXT
            : constants.TRAVELER_INFO_TITLE_SUBHEADING,
          travelerInfoSubtitle: isMobile
            ? constants.TRAVELER_INFO_TITLE_SUBHEADING
            : constants.TRAVELER_INFO_SUBTITLE,
          frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
          additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
          adultTitle: constants.ADULT_TITLE,
          childTitle: constants.CHILD_TITLE,
          infantSeatTitle: constants.INFANT_SEAT_TITLE,
          infantLapTitle: constants.INFANT_LAP_TITLE,
          addTravelers: isMobile
            ? constants.TRAVELER_INFO_TEXT
            : constants.TRAVELER_INFO_TITLE_SUBHEADING,
          completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
          completeTravelerProfileSubtitle: constants.COMPLETE_PROFILE_SUBTITLE,
        }}
        travelers={selectablePassengers}
        tenant={config.TENANT}
        trackEvent={trackEvent}
        onContinue={handleContinue}
        onGoBack={handleBack}
        mobileTravelerRowType="checkbox"
        selectionScreenHeaderElement={
          isPackageCheckoutOptimizationExperiment ? (
            <Typography className="header-text">
              {constants.WHOS_CHECKING_IN_HEADING_V1}
            </Typography>
          ) : (
            <PackagesPriceBreakdown isMobile dropdown />
          )
        }
        customHeader={
          <Box
            className={clsx("packages-hotel-book-passenger-mobile-header", {
              "cx-optimizations-v1": isPackageCheckoutOptimizationExperiment,
            })}
          >
            {!isPackageCheckoutOptimizationExperiment && (
              <Typography className="pax-header-primary-title">
                {constants.WHOS_CHECKING_IN_TITLE}
              </Typography>
            )}

            {isPackageCheckoutOptimizationExperiment && (
              <MobileBookingSteps currentStep={2} />
            )}

            {isPackageCheckoutOptimizationExperiment && <hr />}

            {selectedTravelerCountDiffersFromSearched ? (
              <NotificationBanner
                className="searched-pax-count-mismatch-banner"
                severity={BannerSeverity.WARNING}
                content={
                  <>
                    {constants.SELECTED_PAX_DIFFERS_FROM_SEARCHED_BANNER_TEXT_MOBILE(
                      selectedFlightTravelerIds.length < searchedGuestCount
                    )}
                  </>
                }
              />
            ) : null}
            {!isPackageCheckoutOptimizationExperiment && <hr />}
            <Typography variant="h4" className="pax-header-secondary-title">
              {constants.TRAVELER_INFO_TITLE_SUBHEADING}
            </Typography>

            <Typography className="pax-header-subtitle">
              {constants.TRAVELER_INFO_SUBTITLE}
            </Typography>
          </Box>
        }
        showMobilePopoverTransition={false}
        bypassSelectForSingleTraveler={false}
        showNonModalContent={showNonModalContent}
        bottomContent={
          isPackageCheckoutOptimizationExperiment ? (
            <PackagesPriceBreakdown isMobile popover />
          ) : undefined
        }
        mobileContinueButtonText={
          isPackageCheckoutOptimizationExperiment
            ? constants.MOBILE_CONTINUE_TEXT
            : undefined
        }
        hideBackButton={isTravelApp}
      />
      {!isMobile && (
        <>
          <Typography
            className={clsx("packages-hotel-pax-select-notice", {
              error: validationErrorTypes?.includes("hotel-travelers"),
            })}
          >
            {constants.HOTEL_TRAVELER_NOTICE}
          </Typography>
          <ActionButton
            onClick={handleContinue}
            message={constants.CONTINUE_TEXT}
            className="packages-continue-button"
          />
        </>
      )}
    </>
  );
};
